import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { useEffect, useState } from "react";
import { fadeInUp } from "@/utils/animations";
import { getPostMetas } from "@/utils/blog-handlers";
import type { PostMeta } from "@/utils/blog-handlers";

const Blog = () => {
  const [postMetas, setPostMetas] = useState<PostMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const loadPosts = async (page = 1) => {
    try {
      setLoading(true);
      const result = await getPostMetas(page, 12);
      setPostMetas(result.posts);
      setTotalPages(result.totalPages);
      setTotal(result.total);
      setCurrentPage(result.currentPage);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts(currentPage);
  }, [currentPage]);

  useSEO({
    title: 'Blog | Talos - Insurance Verification Automation',
    description: 'Insights and guides on automating insurance verification for healthcare providers. Learn about EHR integration, eligibility checks, and more.',
    keywords: 'insurance verification blog, healthcare automation, EHR integration, eligibility checks, Talos insights',
    canonical: 'https://runtalos.com/blog',
    ogTitle: 'Blog | Talos',
    ogDescription: 'Insights and guides on automating insurance verification for healthcare providers.',
    ogType: 'website',
    ogUrl: 'https://runtalos.com/blog',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Blog | Talos',
    twitterDescription: 'Insights and guides on automating insurance verification for healthcare providers.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Talos Blog",
      "description": "Insights and guides on automating insurance verification for healthcare providers",
      "url": "https://runtalos.com/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Talos",
        "logo": {
          "@type": "ImageObject",
          "url": "https://runtalos.com/public/logo.png"
        }
      }
    }
  });

  return (
      <div className="bg-white text-zinc-900 p-6 flex-1 flex">
        <div className="mx-auto max-w-5xl w-full flex-1">
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-semibold">Blog</h1>
          </div>

          {/* Blog Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 12 }).map((_, index) => (
                <Card key={index} className="rounded-lg h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <Skeleton className="h-8 w-8 rounded" />
                      <Skeleton className="h-4 w-24" />
                    </div>

                    <Skeleton className="h-8 w-full mb-2" />
                    <Skeleton className="h-8 w-3/4 mb-4" />

                    <div className="space-y-2 mb-6 flex-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>

                    <div className="mt-auto">
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              {postMetas.map((post, index) => {
              const Icon = post.icon ?? FileText;
              return (
                <motion.div
                  key={post.slug}
                  custom={index}
                  variants={fadeInUp}
                  className="w-full"
                >
                  <Link to={`/blog/${post.slug}`} className="block h-full">
                    <Card className="hover:shadow-lg duration-300 ease-out rounded-lg h-full cursor-pointer">
                      <CardContent className="p-6 transition-transform hover:scale-[1.01] duration-300 flex flex-col h-full">
                        <div className="flex items-center gap-3 mb-4">
                          <Icon className="h-8 w-8 text-[#eab308]" />
                          {post.date && (
                            <span className="text-xs uppercase tracking-wide text-zinc-500">
                              {post.date}
                            </span>
                          )}
                        </div>

                        <h3 className="text-2xl font-semibold mb-2 h-16 overflow-hidden" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>{post.title}</h3>
                        <p className="text-zinc-500 text-base mb-6 flex-1 overflow-hidden" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {post.excerpt}
                        </p>

                        <div className="mt-auto">
                          <span className="text-zinc-700 text-base underline">
                            Read more
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
              })}
            </motion.div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex justify-center items-center gap-4">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-zinc-900 text-white'
                          : 'text-zinc-700 bg-white border border-zinc-300 hover:bg-zinc-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}

          {/* Results info */}
          {total > 0 && (
            <div className="mt-6 text-center text-sm text-zinc-500">
              Showing {((currentPage - 1) * 12) + 1} to {Math.min(currentPage * 12, total)} of {total} posts
            </div>
          )}
        </div>
      </div>
  );
};

export default Blog;
